import { each, filter, map, sortBy, uniq, without, find, keyBy } from '@dt/fe-utils/dist/lodash'
import { makeAutoObservable, autorun, runInAction } from 'mobx'
import { payChannelMobx } from '@mobxStore'
import repositoryMobx from '@mobx/mall/repository.mobx'
import { PayApi } from 'api'
import { getPlatform } from '@utils'

const METHOD_SORT = {
  GiftCard: 0,
  MemberCard: 1,
  BankCardOnline: 3,
  ApplePayWeb: 2,
}

class MallPayChannel {
  constructor () {
    makeAutoObservable(this, {
      storeId: false
    })
  }
  /** 门店 id */
  storeId?: number

  /** 场景 */
  useType = 'mall'

  /** 点餐 支付渠道 */
  channels: IPayChannel[] = []

  /** 渠道帐号 */
  channelAccount: Partial<Record<TPayChannelType, Record<string, string>>> = {}

  /** 渠道方式 */
  channelMethod: Partial<Record<TPayChannelType, TPayMethodType>> = {
    GiftCard: 'GiftCard',
    MemberBalance: 'MemberCard',
    CardConnectBankCard: 'BankCardOnline',
    HeartLandBankCard: 'BankCardOnline',
    GlobalPaymentsBankCard: 'BankCardOnline',
    CardConnectApplePay: 'ApplePayWeb',
    GlobalPaymentsApplePay: 'ApplePayWeb',
    StripeApplePay: 'ApplePayWeb',
    PayPalWeb: 'PayPalWeb',
    AlipayApp: 'AlipayApp',
    WechatPayWeb: 'WechatPayWeb',
    ShengPayWechatWeb: 'WechatPayWeb',
    ShengPayAlipayWeb: 'AlipayApp',
    ShengPayWechatWebV2: 'WechatPayWeb',
    ShengPayAlipayWebV2: 'AlipayApp'
  }

  /** 排序 */
  get channelSortMap () {
    const list = this.channels?.map((v: any, index: number) => ({
      method: this.channelMethod?.[v.channelType],
      channel: v.channelType,
      sort: index,
    }))?.filter(c => c.method)
    return keyBy(list, 'method')
  }

  /** 可用的支付方式 */
  get availableMethods (): TPayMethodType[] {
    let methods = map(this.channels, x => this.channelMethod[x.channelType])
    methods = uniq(filter(methods))
    methods = sortBy(methods, x => METHOD_SORT[x])
    const ApplePaySession = (window as any).ApplePaySession
    if (!ApplePaySession) {
      methods = without(methods, 'ApplePayWeb')
    }
    const { isAlipay, isWeixin } = getPlatform()
    if (!isAlipay) {
      methods = without(methods, 'AlipayApp')
    }
    if (!isWeixin) {
      methods = without(methods, 'WechatPayWeb')
    }
    return methods
  }

  /** 可用的外部支付方式 */
  get availableExternalMethods (): TPayMethodType[] {
    return without(this.availableMethods, 'MemberCard', 'GiftCard')
  }

  /** 可使用会员卡 */
  get availableMemberCardMethod (): boolean {
    return !!find(this.availableMethods, a => a === 'MemberCard')
  }

  /** 可使用礼品卡 */
  get availableGiftCardMethod (): boolean {
    return !!find(this.availableMethods, a => a === 'GiftCard')
  }

  /** method map */
  get methodChannel (): Partial<Record<TPayMethodType, IPayChannel>> {
    const result: Partial<Record<TPayMethodType, IPayChannel>> = {}
    each(this.channels, x => {
      const method = this.channelMethod[x.channelType]
      result[method] = x
    })
    return result
  }

  /** 初始化 */
  async init () {
    const { result } = await PayApi.listPaymentTypesStoreV2({
      storeId: 0,
      useTypes: [this.useType],
    }) as any as { result: { [key: string]: IPayChannel[] } }

    const channels = result[this.useType]
    this.channels = channels
    each(channels, async ({ channelType, accountId }) => {
      // Paypal，Alipay,Wechat都无需账号配置。
      if (channelType === 'CardConnectBankCard' || channelType === 'HeartLandBankCard' || channelType === 'GlobalPaymentsBankCard' || channelType === 'StripeApplePay') {
        const { result: account } = await PayApi.listPaymenAccountExt({ accountId }) as any
        runInAction(() => {
          this.channelAccount[channelType] = account
        })
      }
    })
  }
}

const mallPayChannelMobx = new MallPayChannel()

autorun(async () => {
  if (repositoryMobx.info.mallRepositoryId) {
    mallPayChannelMobx.init()
  }
})

export default mallPayChannelMobx
